/**
 * ACTIVITY-CORE (L3) — card for one row in the activity feed.
 *
 * Pure PRESENTATIONAL component: it receives the already-translated sentence,
 * timestamp and labels, and exposes an "Undo" button when the row is
 * revertible (`isRevertibleActivity`). Undo does NOT rewind state: it triggers
 * the server's compensating write (`POST /api/activity/:id/revert`), which
 * returns through PowerSync and refreshes the feed itself (live query). Being
 * presentational, it can be tested without mounting the data layer.
 */
import { Show } from "solid-js";
import { Laptop, Loader2, Smartphone, Undo2, WifiOff } from "@fleet/icons";

export interface ActivityItemProps {
  /** Already-translated human sentence ("You wrote an entry"). */
  sentence: string;
  /** Already-formatted relative time ("3 min ago"). */
  timeLabel: string;
  /** The actor did it on a native device (mobile) vs web/desktop. */
  native: boolean;
  /** The change was made offline and synced much later. */
  offline: boolean;
  offlineLabel: string;
  /** The row can be undone (pre-filter of `isRevertibleActivity`). */
  revertible: boolean;
  /** Its compensating write is in progress (disables the button). */
  reverting: boolean;
  undoLabel: string;
  onRevert: () => void;
}

export function ActivityItem(props: ActivityItemProps) {
  return (
    <article
      class="flex items-start gap-3 rounded-2xl border border-border/50 bg-card/70 p-3 shadow-sm backdrop-blur-sm"
      data-testid="activity-item"
    >
      <span class="mt-0.5 text-muted-foreground/70">
        <Show when={props.native} fallback={<Laptop class="h-4 w-4" />}>
          <Smartphone class="h-4 w-4" />
        </Show>
      </span>
      <div class="flex flex-1 flex-col gap-0.5">
        <p class="text-sm text-foreground">{props.sentence}</p>
        <div class="flex items-center gap-2 text-[11px] text-muted-foreground/70">
          <span>{props.timeLabel}</span>
          <Show when={props.offline}>
            <span
              class="inline-flex items-center gap-1 text-muted-foreground/60"
              data-testid="activity-offline"
            >
              <WifiOff class="h-3 w-3" />
              {props.offlineLabel}
            </span>
          </Show>
        </div>
      </div>
      <Show when={props.revertible}>
        <button
          type="button"
          data-testid="activity-undo"
          class="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          disabled={props.reverting}
          aria-label={props.undoLabel}
          onClick={() => {
            props.onRevert();
          }}
        >
          <Show when={props.reverting} fallback={<Undo2 class="h-3.5 w-3.5" aria-hidden="true" />}>
            <Loader2
              class="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          </Show>
          <span>{props.undoLabel}</span>
        </button>
      </Show>
    </article>
  );
}
